// GICS hierarchy helpers: Sector -> Industry Group -> Industry -> Sub Industry.
// Source list comes from GetApplicationData.gicsSectorMappings (one flat row per sub industry):
// gicsSectorId, gicsSector, industryGroupId, industryGroup, industryId, industry, subIndustryId, subIndustry.
// Engagement rows (utilitydata) and upload payloads use the same keys except `subindustry` (lowercase i);
// edit payloads use `subIndustry`.

export const GICS_ROW_FIELDS = {
    sector: "gicsSector",
    industryGroup: "industryGroup",
    industry: "industry",
    subIndustry: "subindustry",
};

export const GICS_LABELS = {
    sector: "Sector",
    industryGroup: "Industry Group",
    industry: "Industry",
    subIndustry: "Sub Industry",
};

export const EMPTY_GICS_SELECTION = Object.freeze({
    gicsSectorId: null,
    gicsSector: "",
    industryGroupId: null,
    industryGroup: "",
    industryId: null,
    industry: "",
    subIndustryId: null,
    subindustry: "",
});

const normalizeName = (value) =>
    String(value ?? "")
        .trim()
        .replace(/\s+/g, " ")
        .toLowerCase();

const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== "";

const isSameName = (left, right) => hasValue(left) && normalizeName(left) === normalizeName(right);

const isSameId = (left, right) => hasValue(left) && hasValue(right) && String(left) === String(right);

const toMappingList = (mappings) => (Array.isArray(mappings) ? mappings : []);

const uniqueOptions = (items, idKey, nameKey) => {
    const seen = new Map();
    items.forEach((item) => {
        const id = item?.[idKey];
        const name = item?.[nameKey];
        if (!hasValue(id) || !hasValue(name)) return;
        const key = String(id);
        if (!seen.has(key)) seen.set(key, { id, name: String(name).trim() });
    });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
};

export const getGicsSectorOptions = (mappings) =>
    uniqueOptions(toMappingList(mappings), "gicsSectorId", "gicsSector");

export const getGicsIndustryGroupOptions = (mappings, sectorId) =>
    hasValue(sectorId)
        ? uniqueOptions(
            toMappingList(mappings).filter((item) => isSameId(item?.gicsSectorId, sectorId)),
            "industryGroupId",
            "industryGroup"
        )
        : [];

export const getGicsIndustryOptions = (mappings, industryGroupId) =>
    hasValue(industryGroupId)
        ? uniqueOptions(
            toMappingList(mappings).filter((item) => isSameId(item?.industryGroupId, industryGroupId)),
            "industryId",
            "industry"
        )
        : [];

export const getGicsSubIndustryOptions = (mappings, industryId) =>
    hasValue(industryId)
        ? uniqueOptions(
            toMappingList(mappings).filter((item) => isSameId(item?.industryId, industryId)),
            "subIndustryId",
            "subIndustry"
        )
        : [];

// Name-keyed maps, used by the grid cell editors/filters and the Excel export.
export const buildGicsNameHierarchy = (mappings) => {
    const list = toMappingList(mappings);
    const group = (parentKey, childKey) => {
        const grouped = {};
        list.forEach((item) => {
            const parent = String(item?.[parentKey] ?? "").trim();
            const child = String(item?.[childKey] ?? "").trim();
            if (!parent || !child) return;
            if (!grouped[parent]) grouped[parent] = new Set();
            grouped[parent].add(child);
        });
        return Object.keys(grouped)
            .sort((a, b) => a.localeCompare(b))
            .reduce((acc, parent) => {
                acc[parent] = Array.from(grouped[parent]).sort((a, b) => a.localeCompare(b));
                return acc;
            }, {});
    };
    const uniqueNames = (key) =>
        Array.from(new Set(list.map((item) => String(item?.[key] ?? "").trim()).filter(Boolean))).sort(
            (a, b) => a.localeCompare(b)
        );

    return {
        sectors: uniqueNames("gicsSector"),
        industryGroups: uniqueNames("industryGroup"),
        industries: uniqueNames("industry"),
        subIndustries: uniqueNames("subIndustry"),
        industryGroupsBySector: group("gicsSector", "industryGroup"),
        industriesByIndustryGroup: group("industryGroup", "industry"),
        subIndustriesByIndustry: group("industry", "subIndustry"),
    };
};

const pickLevel = (scope, row, { idKey, nameKey, rowIdKey, rowNameKey }) => {
    // Names win over ids because grid edits change the displayed names before ids are refreshed.
    if (hasValue(row?.[rowNameKey])) {
        return scope.find((item) => isSameName(item?.[nameKey], row[rowNameKey])) || null;
    }
    if (hasValue(row?.[rowIdKey])) {
        return scope.find((item) => isSameId(item?.[idKey], row[rowIdKey])) || null;
    }
    return null;
};

const toNullableId = (value) => (hasValue(value) ? value : null);

// Accepts any object carrying the utilitydata-style GICS fields and resolves it against the
// master mappings. Stops at the first level that cannot be matched so partial selections work.
export const resolveGicsSelection = (mappings, rawRow = {}) => {
    const row = normalizeGicsRow(rawRow);
    const list = toMappingList(mappings);
    if (list.length === 0) {
        return {
            gicsSectorId: toNullableId(row?.gicsSectorId),
            gicsSector: row?.gicsSector || "",
            industryGroupId: toNullableId(row?.industryGroupId),
            industryGroup: row?.industryGroup || "",
            industryId: toNullableId(row?.industryId),
            industry: row?.industry || "",
            subIndustryId: toNullableId(row?.subIndustryId),
            subindustry: row?.subindustry || "",
        };
    }

    const result = { ...EMPTY_GICS_SELECTION };

    const sector = pickLevel(list, row, {
        idKey: "gicsSectorId", nameKey: "gicsSector", rowIdKey: "gicsSectorId", rowNameKey: "gicsSector",
    });
    if (!sector) return result;
    result.gicsSectorId = sector.gicsSectorId;
    result.gicsSector = sector.gicsSector;

    const sectorScope = list.filter((item) => isSameId(item?.gicsSectorId, sector.gicsSectorId));
    const industryGroup = pickLevel(sectorScope, row, {
        idKey: "industryGroupId", nameKey: "industryGroup", rowIdKey: "industryGroupId", rowNameKey: "industryGroup",
    });
    if (!industryGroup) return result;
    result.industryGroupId = industryGroup.industryGroupId;
    result.industryGroup = industryGroup.industryGroup;

    const industryGroupScope = sectorScope.filter((item) => isSameId(item?.industryGroupId, industryGroup.industryGroupId));
    const industry = pickLevel(industryGroupScope, row, {
        idKey: "industryId", nameKey: "industry", rowIdKey: "industryId", rowNameKey: "industry",
    });
    if (!industry) return result;
    result.industryId = industry.industryId;
    result.industry = industry.industry;

    const industryScope = industryGroupScope.filter((item) => isSameId(item?.industryId, industry.industryId));
    const subIndustry = pickLevel(industryScope, row, {
        idKey: "subIndustryId", nameKey: "subIndustry", rowIdKey: "subIndustryId", rowNameKey: "subindustry",
    });
    if (!subIndustry) return result;
    result.subIndustryId = subIndustry.subIndustryId;
    result.subindustry = subIndustry.subIndustry;

    return result;
};

// The backend serializes UtilityData.SubIndustry as `subIndustry`; the grid and payload builders read `subindustry`.
export const normalizeGicsRow = (row) => {
    if (!row || row.subindustry !== undefined || row.subIndustry === undefined) return row;
    return { ...row, subindustry: row.subIndustry };
};

export const hasCompleteGicsSelection = (row = {}) =>
    hasValue(row?.gicsSector) && hasValue(row?.industryGroup) && hasValue(row?.industry) && hasValue(row?.subindustry);

// Target Area/Region/Country are only collected for buy-side engagements; the edit dialog hides
// them otherwise, so requiring them elsewhere would reopen the dialog with nothing to fill in.
export const hasRequiredUploadMetadata = (row = {}) => {
    if (!hasCompleteGicsSelection(row) || !hasValue(row?.revenueRange)) return false;
    const isBuySide = String(row?.transactionType || "").trim().toLowerCase() === "buy-side";
    if (!isBuySide) return true;
    return hasValue(row?.targetArea) && hasValue(row?.targetRegion) && hasValue(row?.targetCountry);
};

// Shape used by /editEngagementDetails and /editEngagementDetails/bulk.
export const toGicsEditPayload = (selection = {}) => ({
    gicsSectorId: toNullableId(selection.gicsSectorId),
    gicsSector: selection.gicsSector || "",
    industryGroupId: toNullableId(selection.industryGroupId),
    industryGroup: selection.industryGroup || "",
    industryId: toNullableId(selection.industryId),
    industry: selection.industry || "",
    subIndustryId: toNullableId(selection.subIndustryId),
    subIndustry: selection.subindustry || "",
});

// Shape used by /GenerateMetadata, /ValidateContentFile and /UploadSharePointFilesToBlob.
export const toGicsUploadPayload = (selection = {}) => ({
    gicsSectorId: toNullableId(selection.gicsSectorId),
    gicsSector: selection.gicsSector || "",
    industryGroupId: toNullableId(selection.industryGroupId),
    industryGroup: selection.industryGroup || "",
    industryId: toNullableId(selection.industryId),
    industry: selection.industry || "",
    subIndustryId: toNullableId(selection.subIndustryId),
    subindustry: selection.subindustry || "",
});

// FormData stringifies null to "null", which breaks numeric model binding, so null ids are skipped.
export const appendGicsUploadFields = (formData, selection = {}) => {
    Object.entries(toGicsUploadPayload(selection)).forEach(([key, value]) => {
        if (value === null || value === undefined) return;
        formData.append(key, value);
    });
    return formData;
};
